import { useEffect, useRef } from 'react';
import { SESSION_COMPLETION_STATUS } from '../../../constants/analytics';
import { analytics } from '../../../utils/analytics';
import type { DataChannelMessage } from './useDataChannel';

type CompanionBasic = Partial<{
  mentorId: string | number;
  slug: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
}>;

interface UseCompanionChatAnalyticsParams {
  enabled?: boolean;
  sessionId?: string | null;
  onMessage: (cb: (m: DataChannelMessage) => void) => () => void;
  entryPoint?: string;
  isUserClosed?: boolean;
  companion?: CompanionBasic;
}

// Type guard for user message completion
interface UserMessageCompletedMessage extends DataChannelMessage {
  event: 'message_received';
  data?: {
    message?: {
      role: string;
      action: string;
    };
    item_id?: string;
  };
}

// Helper to detect user message completion
const isUserMessageCompleted = (
  message: DataChannelMessage,
): message is UserMessageCompletedMessage => {
  const msg = message as UserMessageCompletedMessage;
  return (
    msg.event === 'message_received' &&
    msg.data?.message?.role === 'user' &&
    msg.data?.message?.action === 'completed'
  );
};

export const useCompanionChatAnalytics = ({
  enabled = true,
  sessionId,
  onMessage,
  entryPoint,
  isUserClosed,
  companion,
}: UseCompanionChatAnalyticsParams) => {
  const startRef = useRef<number | null>(null);
  const userMsgIdsRef = useRef<Set<string>>(new Set());
  const completionRef = useRef<string>(SESSION_COMPLETION_STATUS.TIMEOUT);
  const endedRef = useRef<boolean>(false);
  const sessionIdRef = useRef<string | null>(null);

  // Store companion data in ref to avoid re-running effect when it changes
  const companionRef = useRef(companion);
  const entryPointRef = useRef(entryPoint);
  const isUserClosedRef = useRef(isUserClosed);

  // Update refs when props change
  useEffect(() => {
    companionRef.current = companion;
  }, [companion]);

  useEffect(() => {
    entryPointRef.current = entryPoint;
  }, [entryPoint]);

  useEffect(() => {
    isUserClosedRef.current = isUserClosed;
  }, [isUserClosed]);

  // Keep the latest isUserClosed status without retriggering effect
  useEffect(() => {
    if (isUserClosed)
      completionRef.current = SESSION_COMPLETION_STATUS.USER_CLOSED;
  }, [isUserClosed]);

  // Keep latest sessionId in a ref
  useEffect(() => {
    sessionIdRef.current = sessionId ?? null;
  }, [sessionId]);

  // Centralized function to send end event - eliminates duplication
  const sendEndEvent = useRef((statusOverride?: string) => {
    if (!startRef.current || !sessionIdRef.current) return;

    const duration = Math.floor((Date.now() - startRef.current) / 1000);
    const propsEnded: Record<string, string | number | boolean> = {
      session_id: sessionIdRef.current,
      session_duration_seconds: duration,
      total_user_messages_sent: userMsgIdsRef.current.size,
      completion_status:
        statusOverride ||
        completionRef.current ||
        SESSION_COMPLETION_STATUS.TIMEOUT,
    };

    analytics.logEvent('chat_session_ended', propsEnded);
  });

  // Subscribe to channel and start analytics on the first received message
  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = onMessage((m: DataChannelMessage) => {
      // Start on the first received message (of any role), provided we have a session id
      if (
        !startRef.current &&
        sessionIdRef.current &&
        (m as any)?.event === 'message_received'
      ) {
        startRef.current = Date.now();
        endedRef.current = false;
        // Default to TIMEOUT - will be overridden to USER_CLOSED if true at this moment
        completionRef.current = isUserClosedRef.current
          ? SESSION_COMPLETION_STATUS.USER_CLOSED
          : SESSION_COMPLETION_STATUS.TIMEOUT;

        const currentCompanion = companionRef.current;
        const currentEntryPoint = entryPointRef.current;
        const sid = sessionIdRef.current;

        const propsStarted: Record<string, string | number | boolean> = {
          session_id: sid!,
          ...(currentEntryPoint ? { entry_point: currentEntryPoint } : {}),
        };

        // Optional companion fields
        const { mentorId, slug, firstName, lastName, jobTitle } =
          currentCompanion || {};

        if (mentorId) propsStarted.companion_id = String(mentorId);
        if (slug) propsStarted.companion_slug = String(slug);

        const fullName = [firstName, lastName].filter(Boolean).join(' ');
        if (fullName) propsStarted.companion_name = fullName;
        if (jobTitle) propsStarted.companion_role = String(jobTitle);

        analytics.logEvent('chat_session_started', propsStarted);
      }

      // Track user messages completion for counts
      if (isUserMessageCompleted(m)) {
        const id = m.data?.item_id ?? `${Date.now()}-${Math.random()}`;
        userMsgIdsRef.current.add(String(id));
      }
    });

    // Capture current values for cleanup
    const cleanupSendEvent = sendEndEvent.current;

    return () => {
      unsubscribe?.();

      if (!startRef.current) return;

      // Only send event if not already finalized
      if (!endedRef.current) {
        cleanupSendEvent();
        endedRef.current = true;
      }

      // Reset state for potential subsequent sessions
      startRef.current = null;
      userMsgIdsRef.current.clear();
    };
  }, [enabled, onMessage]);

  // Proactively finalize if user explicitly closed the session
  useEffect(() => {
    if (!enabled) return;
    if (isUserClosed && startRef.current && !endedRef.current) {
      sendEndEvent.current(SESSION_COMPLETION_STATUS.USER_CLOSED);
      endedRef.current = true;
      // Prepare for a new session if it starts again later
      startRef.current = null;
      userMsgIdsRef.current.clear();
    }
  }, [enabled, isUserClosed]);
};
